// src/lib/split.ts — THE split helper.
//
// Non-negotiable #1: "Splits resolve in one shared helper, never ad hoc in a
// component. Equal split = floor each share to two decimals, then distribute
// the leftover cents one at a time in member id order."
//
// This file is that helper, and it is the only place in the app that decides
// who owes what. The add-expense form calls it to show "¥113.33 each" and
// calls the same function again to produce the rows it writes. If those two
// ever came from different code paths the preview would stop matching the
// receipt, and the database would start rejecting writes at the sum check.
//
// ---------------------------------------------------------------------------
// WHY THE ALLOCATION IS ONE FUNCTION FOR ALL THREE MODES
// ---------------------------------------------------------------------------
// The brief specifies the remainder rule only for equal splits. `shares` and
// the base-currency side of `exact` need a proportional version of the same
// idea, and writing a second rounding rule for those is how the two drift.
//
// So there is one allocator: largest-remainder, ties broken by member id. For
// equal splits every weight is 1, so every fractional remainder is identical,
// so the tie-break decides all of them — and the tie-break is member id order.
// The general rule collapses into exactly the rule the brief asks for, rather
// than merely agreeing with it. See the "reduces to the brief's rule" test.

import { fromCents, toCents } from './money';

export type SplitMode = 'equal' | 'exact' | 'shares';

export interface ResolvedShare {
  memberId: string;
  /** In the expense's own currency. */
  amount: number;
  /** In the trip's base currency, frozen at the expense's rate. */
  baseAmount: number;
}

export interface ResolvedSplit {
  /** Echoes the input, rounded to cents. */
  amount: number;
  /** amount converted at `rate`, rounded to cents. Written to base_amount. */
  baseAmount: number;
  /** One per member, sorted by member id. Sums to amount and baseAmount exactly. */
  shares: ResolvedShare[];
}

export interface SplitIntent {
  /** Amount in the expense's currency. */
  amount: number;
  /**
   * Base-currency units per one unit of the expense currency.
   * base = amount * rate. Same convention as fx_rates.rate and
   * trip_expenses.rate_at_entry — see the sign convention note in
   * db/supabase-schema-steps-2-4.sql section 5.
   */
  rate: number;
  /** Who the expense is split between. Order is irrelevant; ids are sorted. */
  memberIds: string[];
  mode: SplitMode;
  /** mode 'exact': what each member owes, in the expense currency. */
  exactAmounts?: Record<string, number>;
  /** mode 'shares': relative weights, e.g. { alice: 2, bob: 1 }. */
  weights?: Record<string, number>;
}

export class SplitError extends Error {}

/**
 * Distributes `totalCents` across `ids` in proportion to `weights`.
 *
 * Every share is floored, then the leftover cents are handed out one at a
 * time: largest fractional part first, and where fractional parts are equal —
 * which for an equal split is always — in ascending member id order.
 *
 * Guarantees, and they are the whole point: the result sums to exactly
 * `totalCents`, no share is negative, and the same inputs always produce the
 * same output regardless of the order they were passed in.
 */
export function allocate(totalCents: number, ids: string[], weights: number[]): number[] {
  if (ids.length !== weights.length) {
    throw new SplitError('allocate() needs one weight per member');
  }
  if (ids.length === 0) throw new SplitError('Nobody to split between');

  const totalWeight = weights.reduce((a, w) => a + w, 0);
  if (totalWeight <= 0) throw new SplitError('Shares have to add up to more than zero');

  const exact = weights.map((w) => (totalCents * w) / totalWeight);
  const floored = exact.map((v) => Math.floor(v));
  let remainder = totalCents - floored.reduce((a, v) => a + v, 0);

  // Ranked by what each share lost to the floor, then by member id. Sorting
  // indices rather than the values keeps the result aligned with `ids`.
  const order = ids
    .map((id, i) => ({ i, id, frac: exact[i] - floored[i] }))
    .sort((a, b) => (b.frac - a.frac) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const out = [...floored];
  for (let k = 0; remainder > 0; k++, remainder--) {
    out[order[k % order.length].i] += 1;
  }
  return out;
}

/** Sorted, de-duplicated member ids. "Member id order" is defined here. */
function orderedMemberIds(memberIds: string[]): string[] {
  return [...new Set(memberIds)].sort();
}

/**
 * Resolves an intent into the exact rows to write.
 *
 * Throws SplitError with a message worth showing the user — an exact split
 * that doesn't add up is a normal thing for someone to be halfway through
 * typing, not an exceptional condition.
 */
export function resolveSplit(intent: SplitIntent): ResolvedSplit {
  const ids = orderedMemberIds(intent.memberIds);
  if (ids.length === 0) throw new SplitError('Pick at least one person to split this between');

  const amountCents = toCents(intent.amount);
  if (amountCents <= 0) throw new SplitError('Enter an amount greater than zero');
  if (!(intent.rate > 0)) throw new SplitError('Enter an exchange rate greater than zero');

  // Rounded once, here, and then never recomputed — non-negotiable #2. Derived
  // from amountCents rather than from intent.amount so that base and amount
  // can never disagree about what the amount was.
  const baseCents = Math.round(amountCents * intent.rate);
  if (baseCents <= 0) throw new SplitError('That rate converts this to zero');

  let amountShares: number[];
  let weights: number[];

  if (intent.mode === 'exact') {
    const given = intent.exactAmounts ?? {};
    amountShares = ids.map((id) => {
      const cents = toCents(given[id] ?? 0);
      if (cents < 0) throw new SplitError('A share cannot be negative');
      return cents;
    });

    const sum = amountShares.reduce((a, v) => a + v, 0);
    if (sum !== amountCents) {
      throw new SplitError(
        `These add up to ${fromCents(sum).toFixed(2)}. They need to total ${fromCents(amountCents).toFixed(2)}.`,
      );
    }
    // The base side is allocated in proportion to the exact amounts, so that
    // someone who owes twice as much owes twice as much in both currencies.
    weights = amountShares;
  } else if (intent.mode === 'shares') {
    const given = intent.weights ?? {};
    weights = ids.map((id) => {
      const w = Number(given[id] ?? 0);
      if (!Number.isFinite(w) || w < 0) throw new SplitError('Shares have to be zero or more');
      return w;
    });
    amountShares = allocate(amountCents, ids, weights);
  } else {
    weights = ids.map(() => 1);
    amountShares = allocate(amountCents, ids, weights);
  }

  // An exact split where everyone was assigned 0 cannot happen (the sum check
  // above would have caught it), but allocate() would divide by zero, so the
  // fallback keeps the base side defined rather than relying on that.
  const baseWeights = weights.some((w) => w > 0) ? weights : ids.map(() => 1);
  const baseShares = allocate(baseCents, ids, baseWeights);

  return {
    amount: fromCents(amountCents),
    baseAmount: fromCents(baseCents),
    shares: ids.map((memberId, i) => ({
      memberId,
      amount: fromCents(amountShares[i]),
      baseAmount: fromCents(baseShares[i]),
    })),
  };
}

/**
 * What an exact split currently adds up to, for the live "these total X, they
 * need to total Y" line in the form. Separate from resolveSplit because the
 * form needs this on every keystroke, including while it's wrong.
 */
export function exactTotal(memberIds: string[], exactAmounts: Record<string, number>): number {
  const ids = orderedMemberIds(memberIds);
  return fromCents(ids.reduce((sum, id) => sum + toCents(exactAmounts[id] ?? 0), 0));
}
